/**
 * `src/platform/` — the access-token DECODE, and the expiry predicates the session depends on.
 *
 * ⛔ THIS IS A DECODE, NOT A VERIFICATION, and every consumer must treat it that way. The client
 * cannot verify an RS256 signature and must never behave as though it has. Its output drives
 * RENDERING only — `siteRoles`/`sitePermissions` for nav, `sub` for identity tagging, `email` for
 * display. **Every authorization decision is the server's.** A token whose claims say
 * `platform_admin` buys nothing at any endpoint.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * ## ⛔ WHY THIS FILE IS HALF RE-EXPORT AND HALF LOCAL
 *
 * The boundary is drawn at the SYMBOL, not the file: `./auth-utils` contains both something worth
 * passing straight through and something that fails the property this shell requires.
 *
 * ### ✅ PASSED THROUGH — `decodeToken` / `DecodedToken`
 * Its base64url handling is the part worth not rewriting. `atob` accepts only standard base64, so a
 * raw JWT segment containing `-` or `_` (or any segment whose byte length is not a multiple of 3,
 * since padding is stripped) decode-fails silently and `decodeToken` returns `null` — which surfaces
 * as an empty nav with no error anywhere. It also decodes the bytes as UTF-8 rather than Latin-1, so
 * a non-ASCII `email` claim does not come back mojibake'd. Both are earned details; re-deriving them
 * would be re-earning them.
 *
 * ### ⛔ NOT RE-EXPORTED — `isTokenExpired` / `isTokenExpiringSoon`, because they FAIL OPEN
 * Measured in `./auth-utils.ts` (`isTokenExpired`'s and `isTokenExpiringSoon`'s final `return`),
 * and executed with a positive control:
 *
 * ```
 *   return Date.now() >= (decoded.exp - withinSeconds) * 1000;
 *
 *   POSITIVE CONTROL (exp in the past):  isTokenExpired = true    isTokenExpiringSoon = true
 *   THE DEFECT       (exp ABSENT):       isTokenExpired = FALSE   isTokenExpiringSoon = FALSE
 *                                        (undefined - 300) -> NaN ; Date.now() >= NaN -> false
 * ```
 *
 * ⇒ **A token with no `exp` claim reads as "not expired" and "not expiring" — forever.** That is the
 * live session-liveness path: `useAuth`'s proactive-refresh timer calls `isTokenExpiringSoon`, so the
 * session would silently never refresh and every later call would 401 while the UI still said
 * signed-in. WS-C §2.3 states the required property in the opposite direction — *"never toward using
 * a token whose expiry is unknown"* — so that module fails the property its own spec names.
 * `decodeToken` also ends in a bare `payload as DecodedToken`: an unchecked cast of
 * attacker-influenced JSON onto a type declaring `exp: number` as REQUIRED, so TypeScript cannot warn
 * at any call site.
 *
 * ⚠️ **This is NOT a description of a defect to go and fix opportunistically.** The unsafe predicates
 * now live in `./auth-utils` and are deliberately not exported from here; the fail-open itself is
 * filed as **#1188**. It is recorded here as *the reason this boundary guards* — which is precisely
 * what `src/platform/` is for.
 *
 * ⛔ Not exploitable today: `cp-auth-api` always signs an `expiresIn`. It is guarded anyway, because
 * the shell should not depend on an invariant nobody asserts.
 *
 * ### ⚠️ AND A NOTE ON HOW THIS WAS NEARLY MISSED
 * An earlier revision of this file re-exported all three symbols under a docblock reading
 * *"`isTokenExpiringSoon` returns `true` for an undecodable token — fail toward refreshing… Verified
 * in the source rather than assumed."* Every word of that was TRUE, and it verified the **adjacent**
 * branch: the token that does not decode at all. The unsafe case is the token that **decodes fine and
 * has no `exp`**. An artifact asserting a check is what stops the next reader performing it — so state
 * which branch was verified, not merely that verification happened.
 */
import { decodeToken } from './auth-utils';

export { decodeToken };
export type { DecodedToken } from './auth-utils';

/**
 * Whether `exp` is a usable expiry.
 *
 * ⛔ `typeof … === 'number' && Number.isFinite(…)`, NOT `!!exp`. A truthiness test also swallows
 * `exp: 0` — a legitimately-expired epoch token — which would then take the fail-closed branch **by
 * accident rather than by decision**. It reaches "expired" on its own merits below, and the
 * distinction matters the day someone loosens this guard.
 *
 * `Number.isFinite` rejects `NaN` and both infinities, which is the whole point: `NaN` has more than
 * one source (an absent claim, a string claim, a null claim) and the guard must be about FINITENESS
 * rather than about the key existing.
 */
function hasUsableExpiry(exp: unknown): exp is number {
  return typeof exp === 'number' && Number.isFinite(exp);
}

/**
 * Has this access token already expired?
 *
 * ⛔ FAIL-CLOSED on every unknown: an undecodable token, an absent `exp`, or a non-finite one all
 * report `true`. "We cannot tell when this expires" must never resolve to "so keep using it".
 */
export function isTokenExpired(token: string): boolean {
  const decoded = decodeToken(token);
  if (decoded === null) return true;
  if (!hasUsableExpiry(decoded.exp)) return true;
  return Date.now() >= decoded.exp * 1000;
}

/**
 * Will this access token expire within `withinSeconds`?
 *
 * Drives `useAuth`'s proactive-refresh timer, which is why the fail-closed direction is "yes, refresh
 * it": a needless refresh costs one request, whereas a missed one costs the session.
 */
export function isTokenExpiringSoon(token: string, withinSeconds: number): boolean {
  const decoded = decodeToken(token);
  if (decoded === null) return true;
  if (!hasUsableExpiry(decoded.exp)) return true;
  return Date.now() >= (decoded.exp - withinSeconds) * 1000;
}
